import { useState } from 'react';

import { formatMonthLabel, monthsInRange } from '~/shared/dates';
import { selectAll, selectNone, selectWeekdays, selectWeekends } from '~/shared/quickselect';

import { Icon } from './Icons';

interface QuickSelectProps {
  start: string;
  end: string;
  onApply: (next: Set<string>) => void;
}

export function QuickSelect({ start, end, onApply }: QuickSelectProps) {
  const months = monthsInRange(start, end);
  const [activeMonths, setActiveMonths] = useState<Set<string>>(new Set());
  const [showMonths, setShowMonths] = useState(false);

  const mask = activeMonths.size > 0 ? { months: Array.from(activeMonths) } : {};

  const toggleMonth = (m: string) => {
    const next = new Set(activeMonths);
    if (next.has(m)) next.delete(m);
    else next.add(m);
    setActiveMonths(next);
  };

  return (
    <div className="col" style={{ gap: 8 }}>
      <div className="qs-chips">
        <button
          type="button"
          className="qs-chip"
          onClick={() => onApply(selectAll(start, end, mask))}
          aria-label="select every day"
        >
          <Icon name="sparkle" size={14} /> all
        </button>
        <button
          type="button"
          className="qs-chip"
          onClick={() => onApply(selectWeekdays(start, end, mask))}
        >
          weekdays
        </button>
        <button
          type="button"
          className="qs-chip"
          onClick={() => onApply(selectWeekends(start, end, mask))}
        >
          weekends
        </button>
        {months.length > 1 && (
          <button
            type="button"
            className="qs-chip"
            onClick={() => setShowMonths((v) => !v)}
            aria-pressed={showMonths || activeMonths.size > 0}
          >
            limit to {activeMonths.size > 0 ? `${activeMonths.size} mo.` : 'months'}
            <Icon name="chevron-down" size={14} />
          </button>
        )}
        <button
          type="button"
          className="qs-chip qs-chip-clear"
          onClick={() => onApply(selectNone())}
        >
          clear
        </button>
      </div>
      {showMonths && months.length > 1 && (
        <div className="row row-wrap" style={{ gap: 6 }}>
          {months.map((m) => (
            <button
              key={m}
              type="button"
              className="month-pill"
              aria-pressed={activeMonths.has(m)}
              onClick={() => toggleMonth(m)}
            >
              {formatMonthLabel(m)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
